import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { RunOnceScheduler } from 'vscode/vscode/vs/base/common/async';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { createAndFillInContextMenuActions } from 'vscode/vscode/vs/platform/actions/browser/menuEntryActionViewItem';
import { MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IMenuService } from 'vscode/vscode/vs/platform/actions/common/actions.service';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IContextMenuService } from 'vscode/vscode/vs/platform/contextview/browser/contextView.service';
import { IHoverService } from 'vscode/vscode/vs/platform/hover/browser/hover.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { WorkbenchAsyncDataTree } from 'vscode/vscode/vs/platform/list/browser/listService';
import { IOpenerService } from 'vscode/vscode/vs/platform/opener/common/opener.service';
import { IQuickInputService } from 'vscode/vscode/vs/platform/quickinput/common/quickInput.service';
import { ITelemetryService } from 'vscode/vscode/vs/platform/telemetry/common/telemetry.service';
import { IThemeService } from 'vscode/vscode/vs/platform/theme/common/themeService.service';
import { ViewPane } from 'vscode/vscode/vs/workbench/browser/parts/views/viewPane';
import { IViewDescriptorService } from 'vscode/vscode/vs/workbench/common/views.service';
import { CONTEXT_VARIABLE_NAME, CONTEXT_VARIABLE_VALUE, CONTEXT_VARIABLE_TYPE, CONTEXT_VARIABLE_INTERFACES, CONTEXT_VARIABLE_LANGUAGE, CONTEXT_VARIABLE_EXTENSIONID } from 'vscode/vscode/vs/workbench/contrib/debug/common/debug';
import { NotebookVariableDataSource } from './notebookVariablesDataSource.js';
import { NotebookVariablesDelegate, NotebookVariableRenderer, NotebookVariableAccessibilityProvider } from './notebookVariablesTree.js';
import { getNotebookEditorFromEditorPane } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookBrowser';
import { INotebookExecutionStateService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookExecutionStateService.service';
import { INotebookKernelService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookKernelService.service';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';

const _moduleId = "vs/workbench/contrib/notebook/browser/contrib/notebookVariables/notebookVariablesView";
let NotebookVariablesView = class NotebookVariablesView extends ViewPane {
    static { this.ID = 'notebookVariablesView'; }
    static { this.TITLE = ( localize2WithPath(_moduleId, 0, "Notebook Variables")); }
    constructor(options, editorService, notebookKernelService, notebookExecutionStateService, keybindingService, contextMenuService, contextKeyService, configurationService, instantiationService, viewDescriptorService, openerService, quickInputService, commandService, themeService, telemetryService, hoverService, menuService) {
        super(options, keybindingService, contextMenuService, configurationService, contextKeyService, viewDescriptorService, instantiationService, openerService, themeService, telemetryService, hoverService);
        this.editorService = editorService;
        this.notebookKernelService = notebookKernelService;
        this.notebookExecutionStateService = notebookExecutionStateService;
        this.quickInputService = quickInputService;
        this.commandService = commandService;
        this.menuService = menuService;
        this._register(this.editorService.onDidActiveEditorChange(this.handleActiveEditorChange.bind(this)));
        this._register(this.notebookKernelService.onDidNotebookVariablesUpdate(this.handleVariablesChanged.bind(this)));
        this._register(this.notebookExecutionStateService.onDidChangeExecution(this.handleExecutionStateChange.bind(this)));
        this.setActiveNotebook();
        this.dataSource = ( (new NotebookVariableDataSource(this.notebookKernelService)));
        this.updateScheduler = ( (new RunOnceScheduler(() => this.tree?.updateChildren(), 100)));
    }
    renderBody(container) {
        super.renderBody(container);
        this.element.classList.add('debug-pane');
        this.tree = this.instantiationService.createInstance(WorkbenchAsyncDataTree, 'notebookVariablesTree', container, ( (new NotebookVariablesDelegate())), [( (new NotebookVariableRenderer(this.hoverService)))], this.dataSource, {
            accessibilityProvider: ( (new NotebookVariableAccessibilityProvider())),
            identityProvider: { getId: (e) => e.id },
        });
        this.tree.layout();
        if (this.activeNotebook) {
            this.tree.setInput({ kind: 'root', notebook: this.activeNotebook });
        }
        this._register(this.tree.onContextMenu(e => this.onContextMenu(e)));
    }
    onContextMenu(e) {
        if (!e.element) {
            return;
        }
        const element = e.element;
        const arg = {
            source: ( (element.notebook.uri.toString())),
            name: element.name,
            value: element.value,
            type: element.type,
            expression: element.expression,
            language: element.language,
            extensionId: element.extensionId
        };
        const actions = [];
        const overlayedContext = this.contextKeyService.createOverlay([
            [CONTEXT_VARIABLE_NAME.key, element.name],
            [CONTEXT_VARIABLE_VALUE.key, element.value],
            [CONTEXT_VARIABLE_TYPE.key, element.type],
            [CONTEXT_VARIABLE_INTERFACES.key, element.interfaces],
            [CONTEXT_VARIABLE_LANGUAGE.key, element.language],
            [CONTEXT_VARIABLE_EXTENSIONID.key, element.extensionId]
        ]);
        const menu = this.menuService.createMenu(MenuId.NotebookVariablesContext, overlayedContext);
        createAndFillInContextMenuActions(menu, { arg, shouldForwardArgs: true }, actions);
        menu.dispose();
        this.contextMenuService.showContextMenu({
            getAnchor: () => e.anchor,
            getActions: () => actions
        });
    }
    layoutBody(height, width) {
        super.layoutBody(height, width);
        this.tree?.layout(height, width);
    }
    setActiveNotebook() {
        const current = this.activeNotebook;
        const activeEditorPane = this.editorService.activeEditorPane;
        if (activeEditorPane?.getId() === 'workbench.editor.notebook' || activeEditorPane?.getId() === 'workbench.editor.interactive') {
            const notebookDocument = getNotebookEditorFromEditorPane(activeEditorPane)?.getViewModel()?.notebookDocument;
            this.activeNotebook = notebookDocument;
        }
        return current !== this.activeNotebook;
    }
    handleActiveEditorChange() {
        if (this.setActiveNotebook() && this.activeNotebook) {
            this.tree?.setInput({ kind: 'root', notebook: this.activeNotebook });
            this.updateScheduler.schedule();
        }
    }
    handleExecutionStateChange(event) {
        if (this.activeNotebook) {
            if (event.affectsNotebook(this.activeNotebook.uri)) {
                this.dataSource.cancel();
                if (event.changed === undefined) {
                    this.updateScheduler.schedule();
                }
                else {
                    this.updateScheduler.cancel();
                }
            }
        }
    }
    handleVariablesChanged(notebookUri) {
        if (this.activeNotebook && ( (notebookUri.toString())) === ( (this.activeNotebook.uri.toString()))) {
            this.tree?.setInput({ kind: 'root', notebook: this.activeNotebook });
            this.updateScheduler.schedule();
        }
    }
};
NotebookVariablesView = ( (__decorate([
    ( (__param(1, IEditorService))),
    ( (__param(2, INotebookKernelService))),
    ( (__param(3, INotebookExecutionStateService))),
    ( (__param(4, IKeybindingService))),
    ( (__param(5, IContextMenuService))),
    ( (__param(6, IContextKeyService))),
    ( (__param(7, IConfigurationService))),
    ( (__param(8, IInstantiationService))),
    ( (__param(9, IViewDescriptorService))),
    ( (__param(10, IOpenerService))),
    ( (__param(11, IQuickInputService))),
    ( (__param(12, ICommandService))),
    ( (__param(13, IThemeService))),
    ( (__param(14, ITelemetryService))),
    ( (__param(15, IHoverService))),
    ( (__param(16, IMenuService)))
], NotebookVariablesView)));

export { NotebookVariablesView };
